import { generateCodeVerifier, deriveCodeChallenge } from '../lib/pkce'
import { apiFetch, API_BASE } from '../lib/apiClient'
import { logger } from '../utils/logger'
import { setAuthTokenCookie, clearAuthTokenCookie } from '../utils/authToken'

export type OAuthProvider = 'github' | 'google' | 'linkedin'

export type SerializedUser = {
  id: string
  name?: string
  email?: string
  avatarUrl?: string
  provider: OAuthProvider
  subscription?: string
  intakeCompleted?: boolean
  interests?: string[]
  learningGoals?: string
  skillLevel?: string
}

// Helpers to encode/decode OAuth state payload without introducing dependencies
function encodeState(payload: object): string {
  try {
    return btoa(encodeURIComponent(JSON.stringify(payload)))
  } catch {
    return ''
  }
}
function decodeState<T = object>(state: string | undefined | null): T | null {
  if (!state) return null
  try {
    return JSON.parse(decodeURIComponent(atob(state))) as T
  } catch {
    return null
  }
}

const OAUTH_CONFIG: Record<OAuthProvider, {
  authUrl: string
  clientId: string | undefined
  scope: string
  extraParams?: Record<string, string>
}> = {
  github: {
    authUrl: 'https://github.com/login/oauth/authorize',
    clientId: (import.meta as { env?: { VITE_GITHUB_CLIENT_ID?: string } }).env?.VITE_GITHUB_CLIENT_ID,
    scope: 'read:user user:email',
  },
  google: {
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: (import.meta as { env?: { VITE_GOOGLE_CLIENT_ID?: string } }).env?.VITE_GOOGLE_CLIENT_ID,
    scope: 'openid email profile',
    extraParams: {
      access_type: 'online',
      include_granted_scopes: 'true',
      prompt: 'consent',
    },
  },
  linkedin: {
    authUrl: 'https://www.linkedin.com/oauth/v2/authorization',
    clientId: (import.meta as { env?: { VITE_LINKEDIN_CLIENT_ID?: string } }).env?.VITE_LINKEDIN_CLIENT_ID,
    // Use OpenID Connect scopes (requires "Sign In with LinkedIn using OpenID Connect" product)
    scope: 'openid profile email',
    extraParams: {
      response_mode: 'query',
    },
  },
}

const STORAGE = {
  STATE: 'auth:state',
  VERIFIER: 'auth:verifier',
  PROVIDER: 'auth:provider',
  USER: 'auth:user',
}

export function getRedirectUri() {
  return `${window.location.origin}/auth/callback`
}

function saveSession(key: string, value: string) {
  // Use localStorage instead of sessionStorage to persist across OAuth redirects
  localStorage.setItem(key, value)
}

function readSession(key: string) {
  // Read from localStorage instead of sessionStorage
  return localStorage.getItem(key) || undefined
}

function clearSession() {
  // Clear from localStorage instead of sessionStorage
  localStorage.removeItem(STORAGE.STATE)
  localStorage.removeItem(STORAGE.VERIFIER)
  localStorage.removeItem(STORAGE.PROVIDER)
}

export function getUser(): SerializedUser | null {
  const raw = localStorage.getItem(STORAGE.USER)
  if (!raw) return null
  try { 
    const parsed = JSON.parse(raw) as SerializedUser
    return parsed
  } catch (e) { 
    logger.error('Failed to parse user from localStorage', { error: e })
    return null 
  }
}

/**
 * Refresh user data from the backend and update stored user/token
 * Used after payment success to get updated subscription status
 */
export async function refreshUser(): Promise<SerializedUser | null> {
  const currentUser = getUser()
  if (!currentUser?.id) {
    logger.warn('Cannot refresh user - no current user')
    return null
  }

  try {
    const result = await apiFetch<AuthResult>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ userId: currentUser.id }),
    })

    if (result?.user && result?.token) {
      // Update stored user and token
      localStorage.setItem(STORAGE.USER, JSON.stringify(result.user))
      setAuthTokenCookie(result.token)
      logger.info('User data refreshed successfully', { userId: result.user.id })
      return result.user
    }

    return null
  } catch (e) {
    logger.error('Failed to refresh user data', { error: e })
    return null
  }
}

export function logout() {
  localStorage.removeItem(STORAGE.USER)
  try { localStorage.removeItem('auth:token') } catch {
    // Ignore localStorage errors
  }
  clearAuthTokenCookie()
  // Also clear any lingering session data
  clearSession()
}

// Temporarily unused but kept for future use
// function getBackendOrigin(): string {
//   try {
//     const url = new URL(API_BASE || '')
//     if (url.origin && url.origin !== 'null') return url.origin
//   } catch {
//     // Ignore URL parsing errors
//   }
//   return 'https://api-auth.technyc.io'
// }

export async function login(provider: OAuthProvider, isSignup: boolean = false) {
  const cfg = OAUTH_CONFIG[provider]
  
  if (!cfg?.clientId) {
    logger.warn('Missing OAuth client ID', { provider, envVar: `VITE_${provider.toUpperCase()}_CLIENT_ID` })
  }

  const verifier = generateCodeVerifier()
  const challenge = await deriveCodeChallenge(verifier)
  const stateId = crypto.randomUUID()

  // Use stored redirect from ProtectedRoute, or default to /courses
  const storedRedirect = localStorage.getItem('auth:redirect')
  const redirectTo = storedRedirect || '/courses'

  // Encode ALL OAuth parameters in state to avoid localStorage issues
  const statePayload = { 
    id: stateId, 
    redirectTo, 
    isSignup,
    provider,  // Include provider in state
    verifier,  // Include verifier in state
    challenge  // Include challenge for verification
  }
  const stateParam = encodeState(statePayload)

  // Persist parameters for callback validation
  saveSession(STORAGE.STATE, stateParam)
  saveSession(STORAGE.VERIFIER, verifier)
  saveSession(STORAGE.PROVIDER, provider)

  // Testing without form_post - use direct callback
  const redirectUri = getRedirectUri()

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId || 'PLACEHOLDER_CLIENT_ID',
    redirect_uri: redirectUri,
    scope: cfg.scope,
    state: stateParam,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  })
  if (cfg.extraParams) Object.entries(cfg.extraParams).forEach(([k, v]) => params.set(k, v))

  const url = `${cfg.authUrl}?${params.toString()}`
  
  // External navigation to provider (expected full navigation)
  window.location.assign(url)
}

export type AuthResult = { user: SerializedUser; token: string }

export async function handleCallback(): Promise<AuthResult & { redirectTo?: string; isNewUser?: boolean }> {
  const provider = readSession(STORAGE.PROVIDER) as OAuthProvider | undefined
  const expectedState = readSession(STORAGE.STATE)
  const verifier = readSession(STORAGE.VERIFIER)

  const url = new URL(window.location.href)
  const code = url.searchParams.get('code') || undefined
  const state = url.searchParams.get('state') || undefined
  const error = url.searchParams.get('error') || undefined

  // Decode ALL parameters from state
  const statePayload = decodeState<{ 
    id: string; 
    redirectTo?: string; 
    isSignup?: boolean;
    provider?: string;
    verifier?: string;
    challenge?: string;
  }>(state)
  const redirectToFromState = statePayload?.redirectTo
  const isSignupFromState = statePayload?.isSignup || false
  
  // Try to get provider and verifier from state first, fallback to localStorage
  const providerFromState = statePayload?.provider as OAuthProvider | undefined
  const verifierFromState = statePayload?.verifier
  
  // Only log errors, not successful flows
  if (error) {
    logger.error('OAuth error from provider', { error })
  }

  if (error) throw new Error(`OAuth error: ${error}`)
  
  // Use provider and verifier from state if available, otherwise from localStorage
  const finalProvider = providerFromState || provider
  const finalVerifier = verifierFromState || verifier
  

  
  if (!code || !finalProvider || !finalVerifier || !state) {
    logger.error('Missing OAuth parameters', {
      hasCode: !!code,
      hasProvider: !!finalProvider,
      providerSource: providerFromState ? 'state' : 'localStorage',
      hasVerifier: !!finalVerifier,
      verifierSource: verifierFromState ? 'state' : 'localStorage',
      hasState: !!state,
      statePayload
    })
    throw new Error('Missing oauth params')
  }
  
  // Validate state ID matches if we have expectedState
  if (expectedState && state !== expectedState && statePayload?.id !== (decodeState(expectedState) as { id?: string })?.id) {
    throw new Error('Invalid state')
  }

  // Attempt to exchange the code with your backend
  let result: AuthResult | null = null
  if (API_BASE) {
    try {
      // Use the same redirect_uri that was used in the initial OAuth request
      const redirectUriForCallback = getRedirectUri();
      
      result = await apiFetch<AuthResult>(`/api/auth/${finalProvider}/callback`, {
        method: 'POST',
        body: JSON.stringify({ 
          code, 
          code_verifier: finalVerifier, 
          redirect_uri: redirectUriForCallback,
          isSignup: isSignupFromState 
        }),
      })
    } catch (e: unknown) {
      // Check if this is a specific auth error we need to handle
      const errorWithData = e as { data?: { error?: string; message?: string }; message?: string }
      if (errorWithData?.data?.error === 'USER_NOT_FOUND') {
        logger.info('User not found, needs signup', { provider });
        // Store error info for the callback page to handle
        localStorage.setItem('auth:error', JSON.stringify({
          type: 'USER_NOT_FOUND',
          provider,
          message: errorWithData.data.message || 'User not found'
        }));
        throw new Error('USER_NOT_FOUND');
      } else if (errorWithData?.data?.error === 'USER_ALREADY_EXISTS') {
        logger.info('User already exists, needs signin', { provider });
        localStorage.setItem('auth:error', JSON.stringify({
          type: 'USER_ALREADY_EXISTS',
          provider,
          message: errorWithData.data.message || 'User already exists'
        }));
        throw new Error('USER_ALREADY_EXISTS');
      }
      
      logger.error('Backend OAuth exchange failed', {
        error: errorWithData?.message || String(e),
        provider
      });
      throw e;
    }
  } else {
    logger.error('No API_BASE configured')
  }

  if (!result) {
    // OAuth failed - throw error instead of creating mock user
    logger.error('OAuth authentication failed', { provider })
    throw new Error('Authentication failed. Please try again.')
  }

  // Persist user & token
  localStorage.setItem(STORAGE.USER, JSON.stringify(result.user))
  setAuthTokenCookie(result.token)
  try { localStorage.removeItem('auth:token') } catch { /* ignore */ }

  // Cleanup session (defensive - only clear if we actually processed)
  if (code && provider && expectedState && verifier) {
    clearSession()
  }

  // Use redirect from state, or default to /courses
  const finalRedirect = redirectToFromState || '/courses'
  // Clean any stored redirect hint
  try { localStorage.removeItem('auth:redirect') } catch { /* ignore */ }

  return { ...result, redirectTo: finalRedirect, isNewUser: isSignupFromState }
}

// Helper function to get auth error from localStorage
export function getAuthError(): { type: string; provider: string; message: string } | null {
  const errorStr = localStorage.getItem('auth:error')
  if (!errorStr) return null
  try {
    return JSON.parse(errorStr)
  } catch {
    return null
  }
}

// Helper function to clear auth error
export function clearAuthError(): void {
  localStorage.removeItem('auth:error')
}

// Removed simulateLogin function - authentication must go through real OAuth
